import { formatInVenueZone } from 'utils/shared/time/formatInVenueZone';
import { parseClock } from 'utils/shared/time/splitIntoHourChunks';

/** "10:00 – 11:00 AM" in the venue's zone (browser zone when none is known). */
export const formatVenueTimeRange = (start: string, end: string, timezone?: string | null) => {
  const fmt = (date: string, pattern: string) => formatInVenueZone(date, pattern, timezone);
  return `${fmt(start, 'h:mm')} – ${fmt(end, 'h:mm a')}`;
};

/** "10 – 11 AM" / "10 AM – 6 PM" / "10:30 – 11 AM": hours only when the minutes are :00. */
export const formatCompactTimeRange = (
  start: string,
  end: string,
  fmt: (date: string, pattern: string) => string,
) => {
  const hour = (date: string) => (fmt(date, 'mm') === '00' ? fmt(date, 'h') : fmt(date, 'h:mm'));
  const sameMeridiem = fmt(start, 'a') === fmt(end, 'a');
  return sameMeridiem
    ? `${hour(start)} – ${hour(end)} ${fmt(end, 'a')}`
    : `${hour(start)} ${fmt(start, 'a')} – ${hour(end)} ${fmt(end, 'a')}`;
};

/** `formatCompactTimeRange` for wall-clock "HH:mm" strings (a weekly slot's hours). */
export const formatClockRange = (start: string, end: string) =>
  formatCompactTimeRange(
    parseClock(start).toISOString(),
    parseClock(end).toISOString(),
    (date, pattern) => formatInVenueZone(date, pattern),
  );

/**
 * The chat header's second line: "Sep 24 • Wed • 10 – 11 AM" for a single day,
 * "Apr 20 – Apr 21 • 10 AM – 6 PM" across days. Venue-local.
 */
export const formatVenueDateParts = (start: string, end: string, timezone?: string | null) => {
  const fmt = (date: string, pattern: string) => formatInVenueZone(date, pattern, timezone);
  const isSameDay = fmt(start, 'yyyy-MM-dd') === fmt(end, 'yyyy-MM-dd');
  const day = isSameDay
    ? [fmt(start, 'MMM d'), fmt(start, 'EEE')]
    : [`${fmt(start, 'MMM d')} – ${fmt(end, 'MMM d')}`];
  return [...day, formatCompactTimeRange(start, end, fmt)];
};

export const formatVenueDateLine = (start: string, end: string, timezone?: string | null) =>
  formatVenueDateParts(start, end, timezone).join(' • ');
